// Lines saved by Clear cart so an accidental clear can be undone from the empty drawer in this tab.
const CartRecovery = {
  key: 'cart-drawer:cleared-cart',
  maxAge: 60 * 60 * 1000,

  save(items) {
    try {
      sessionStorage.setItem(this.key, JSON.stringify({ items, savedAt: Date.now() }));
    } catch (error) {}
  },

  read() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.key));
      if (saved?.items?.length && Date.now() - saved.savedAt < this.maxAge) return saved.items;
    } catch (error) {}
    this.clear();
    return null;
  },

  clear() {
    try {
      sessionStorage.removeItem(this.key);
    } catch (error) {}
  },

  // Raw line data from the Ajax API; Liquid escapes property values, so it can't supply them.
  async fetchLines() {
    try {
      const response = await fetch(`${routes.cart_url.replace(/\/$/, '')}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      return cart.items.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
        ...(item.selling_plan_allocation && { selling_plan: item.selling_plan_allocation.selling_plan.id }),
      }));
    } catch (error) {
      return null;
    }
  },
};

// Rendered only in a non-empty drawer: any cart with items supersedes a cleared one.
class CartRecoveryReset extends HTMLElement {
  connectedCallback() {
    CartRecovery.clear();
  }
}

customElements.define('cart-recovery-reset', CartRecoveryReset);

// Rendered in the empty drawer; only shown when there is a cleared cart to restore.
class CartRecoverButton extends HTMLElement {
  connectedCallback() {
    this.hidden = !CartRecovery.read();
  }
}

customElements.define('cart-recover-button', CartRecoverButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Delegate on the persistent wrapper: section rendering replaces the items element.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-clear]')) this.querySelector('cart-drawer-items').clearCart();
      const recoverButton = event.target.closest('[data-cart-recover]');
      if (recoverButton) this.recoverCart(recoverButton);
    });
  }

  async recoverCart(button) {
    const items = CartRecovery.read();
    if (!items || this.recovering) return;
    this.recovering = true;
    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');

    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const addItems = async (lines) => {
      const response = await fetch(`${routes.cart_add_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({ items: lines, sections: sectionIds, sections_url: window.location.pathname }),
      });
      const state = await response.json();
      return response.ok && !state.status ? state : null;
    };

    let recovered = false;
    let addedAny = false;
    try {
      let state = await addItems(items);
      addedAny = !!state;
      let unavailable = false;
      if (!state) {
        // One unavailable line rejects the whole batch; restore what is still available.
        for (const line of items) {
          if (await addItems([line])) addedAny = true;
          else unavailable = true;
        }
        const url = new URL(routes.cart_url, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(window.cartStrings.error);
        state = { sections: await response.json() };
      }

      CartRecovery.clear();
      // Render without renderContents(): its open() leaves a focus-trap listener behind when already open.
      this.getSectionsToRender().forEach((section) => {
        const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (element) element.innerHTML = this.getSectionInnerHTML(state.sections[section.id], section.selector);
      });
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      const empty = !!this.querySelector('.drawer__inner-empty');
      this.classList.toggle('is-empty', empty);

      if (unavailable) {
        // The empty layout keeps the error area off screen, so report there only when items came back.
        const notice = document.createElement('p');
        notice.className = 'cart-drawer__notice';
        // #CartDrawer-CartErrors is already an alert region.
        if (empty) notice.setAttribute('role', 'alert');
        notice.textContent = button.dataset.partialMessage;
        const target = empty ? this.querySelector('.drawer__inner-empty .cart-drawer__actions') : this.querySelector('#CartDrawer-CartErrors');
        target?.replaceChildren(notice);
      }
      const container = this.querySelector(empty ? '.drawer__inner-empty' : '.drawer__inner');
      if (this.classList.contains('active') && container) trapFocus(container, empty ? container.querySelector('a, button') : container);
      recovered = true;
    } catch (error) {
      if (addedAny) {
        // Items are already back in the cart; a retry would add them twice. The next render shows them.
        CartRecovery.clear();
      } else {
        button.disabled = false;
        button.focus();
      }
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    } finally {
      this.recovering = false;
    }

    // Separate from the UI above so a failure here can't undo a successful recovery. Subscribers
    // (e.g. price-per-item) expect full cart JSON, which the add and section responses don't provide.
    if (!recovered) return;
    try {
      const response = await fetch(`${routes.cart_url.replace(/\/$/, '')}.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: await response.json() });
    } catch (error) {
      console.error(error);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onChange(event) {
    const drawer = this.closest('cart-drawer');
    // Ignore delayed quantity events from before a clear or section replacement.
    if (!this.isConnected || drawer?.clearing || drawer?.quantityRequest) return;
    super.onChange(event);
  }

  updateQuantity(...args) {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.clearing || drawer.quantityRequest) return;
    // After a successful clear with failed rendering, old line indexes are stale.
    if (drawer.clearedCartState) return this.clearCart();
    super.updateQuantity(...args);
  }

  setControlsBusy(drawer, clearing) {
    drawer.cartControls ||= new Map();
    drawer.querySelectorAll('button, input, textarea').forEach((control) => {
      if (control.classList.contains('drawer__close') || (!clearing && control.matches('[data-cart-clear]'))) return;
      if (!drawer.cartControls.has(control)) drawer.cartControls.set(control, control.disabled);
      control.disabled = true;
    });
    drawer.querySelector('cart-drawer-items')?.setAttribute('aria-busy', 'true');
  }

  restoreControls(drawer) {
    drawer.cartControls?.forEach((disabled, control) => {
      if (control.isConnected) control.disabled = disabled;
    });
    drawer.cartControls = null;
    drawer.querySelector('cart-drawer-items')?.removeAttribute('aria-busy');
  }

  enableLoading(line) {
    this.requestDrawer = this.closest('cart-drawer');
    this.requestDrawer.quantityRequest = new Promise((resolve) => {
      this.finishQuantityRequest = resolve;
    });
    super.enableLoading(line);
    this.setControlsBusy(this.requestDrawer, false);
  }

  disableLoading(line) {
    // The base update replaces this element, so use the saved persistent wrapper.
    try {
      super.disableLoading(line);
    } finally {
      const drawer = this.requestDrawer;
      this.restoreControls(drawer);
      if (drawer.clearing) this.setControlsBusy(drawer, true);
      drawer.quantityRequest = null;
      this.finishQuantityRequest();
    }
  }

  async clearCart() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.clearing) return;
    drawer.clearing = true;
    this.setControlsBusy(drawer, true);
    const showLoading = () => {
      drawer.querySelector('[data-cart-clear]')?.classList.add('loading');
      drawer.querySelector('[data-cart-clear] .loading__spinner')?.classList.remove('hidden');
      drawer.querySelector('#CartDrawer-LineItemStatus')?.setAttribute('aria-hidden', 'false');
      const errors = drawer.querySelector('#CartDrawer-CartErrors');
      if (errors) errors.textContent = '';
    };
    showLoading();
    try {
      // Base CartItems finishes its POST, rendering and finally block before clearing.
      await drawer.quantityRequest;
      this.setControlsBusy(drawer, true);
      showLoading();
      const sections = this.getSectionsToRender();
      let state = drawer.clearedCartState;
      if (!state) {
        // Read after pending quantity updates finish, so it matches what is cleared. Failure only skips undo.
        const clearedItems = await CartRecovery.fetchLines();
        const response = await fetch(`${routes.cart_url.replace(/\/$/, '')}/clear.js`, {
          ...fetchConfig(),
          body: JSON.stringify({
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        state = await response.json();
        if (!response.ok || state.errors || state.item_count !== 0) throw new Error(window.cartStrings.error);
        // Saved before rendering so the empty drawer's Recover cart button sees it.
        if (clearedItems?.length) CartRecovery.save(clearedItems);
        // Retain the successful mutation if rendering fails; retries must only GET.
        drawer.clearedCartState = state;
      }
      const parseSections = (html) => sections.map((section) => {
        const source = html?.[section.section];
        return typeof source === 'string'
          ? new DOMParser().parseFromString(source, 'text/html').querySelector(section.selector)
          : null;
      });
      let rendered = parseSections(state.sections);
      if (rendered.some((section) => !section)) {
        const url = new URL(routes.cart_url, window.location.origin);
        url.searchParams.set('sections', sections.map((section) => section.section).join(','));
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(window.cartStrings.error);
        state.sections = await response.json();
        rendered = parseSections(state.sections);
      }
      if (rendered.some((section) => !section)) throw new Error(window.cartStrings.error);
      sections.forEach((section, index) => {
        const container = document.getElementById(section.id);
        const target = container.querySelector(section.selector) || container;
        target.innerHTML = rendered[index].innerHTML;
      });
      const empty = !!drawer.querySelector('.drawer__inner-empty');
      drawer.classList.toggle('is-empty', empty);
      drawer.clearedCartState = null;
      if (drawer.classList.contains('active')) {
        const container = drawer.querySelector(empty ? '.drawer__inner-empty' : '.drawer__inner');
        trapFocus(container, container.querySelector('a, button'));
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    } catch (error) {
      const errors = drawer.querySelector('#CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
      if (drawer.clearedCartState) {
        drawer.clearedCartState.sections = null;
        const button = drawer.querySelector('[data-cart-clear]');
        const label = button?.querySelector('[data-cart-clear-label]');
        if (label) label.textContent = button.dataset.retryLabel;
      }
    } finally {
      drawer.clearing = false;
      this.restoreControls(drawer);
      drawer.querySelector('[data-cart-clear]')?.classList.remove('loading');
      drawer.querySelector('[data-cart-clear] .loading__spinner')?.classList.add('hidden');
      drawer.querySelector('#CartDrawer-LineItemStatus')?.setAttribute('aria-hidden', 'true');
      if (drawer.classList.contains('active') && drawer.querySelector('#CartDrawer-CartErrors')?.textContent) {
        drawer.querySelector('[data-cart-clear]')?.focus();
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
